import { LitElement, html } from 'lit';
import styles from './studio-checkbox.css.mjs';

export class StudioCheckbox extends LitElement {
  static styles = styles;

  static properties = {
    position: { type: String, reflect: true },
    state: { type: String, reflect: true },
    text: { type: String, reflect: true },
    checked: { type: Boolean, reflect: true },
    indeterminate: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
  };

  static validPositions = ['left', 'right'];
  static validStates = ['default', 'hover', 'focus', 'press', 'selected', 'indeterminate', 'disabled'];

  constructor() {
    super();
    this.position = 'left';
    this.state = 'default';
    this.text = '';
    this.checked = false;
    this.indeterminate = false;
    this.disabled = false;
    this._eventListeners = [];
  }

  set position(value) {
    this._position = StudioCheckbox.validPositions.includes(value) ? value : 'left';
  }

  get position() {
    return this._position;
  }

  set state(value) {
    this._state = StudioCheckbox.validStates.includes(value) ? value : 'default';
  }

  get state() {
    return this._state;
  }

  set text(value) {
    this._text = typeof value === 'string' ? value : '';
  }

  get text() {
    return this._text;
  }

  firstUpdated() {
    const wrapper = this.renderRoot.querySelector('.checkbox-wrapper');
    if (!wrapper) return;

    const setState = (value) => {
      if (!this.disabled && this.state !== 'selected' && this.state !== 'indeterminate') {
        this.state = value;
      }
    };

    this._eventListeners = [
      { event: 'mouseenter', handler: () => setState('hover') },
      { event: 'mouseleave', handler: () => {
        if (!this.disabled) {
          this.state = this.checked ? 'selected' : 'default';
        }
      }},
      { event: 'focus', handler: () => setState('focus') },
      { event: 'blur', handler: () => {
        if (!this.disabled) {
          this.state = this.checked ? 'selected' : 'default';
        }
      }},
      { event: 'mousedown', handler: () => setState('press') },
      { event: 'active', handler: () => setState('press') },
      { event: 'target', handler: () => setState('press') },
      { event: 'mouseup', handler: () => setState('focus') },
    ];

    this._eventListeners.forEach(({ event, handler }) =>
      wrapper.addEventListener(event, handler)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const wrapper = this.renderRoot.querySelector('.checkbox-wrapper');
    if (wrapper && this._eventListeners) {
      this._eventListeners.forEach(({ event, handler }) =>
        wrapper.removeEventListener(event, handler)
      );
    }
  }

  render() {
    const isSelected = this.checked || this.state === 'selected';
    const isIndeterminate = this.indeterminate || this.state === 'indeterminate';
    const isDisabled = this.disabled || this.state === 'disabled';

    return html`
      <label class="checkbox-wrapper" tabindex="${isDisabled ? -1 : 0}" aria-disabled="${isDisabled}">
        ${this.position === 'left' ? html`
          <slot name="label">
            ${this.text ? html`<span class="label">${this.text}</span>` : ''}
          </slot>
        ` : ''}
        <input
          type="checkbox"
          ?checked="${isSelected}"
          ?indeterminate="${isIndeterminate}"
          ?disabled="${isDisabled}"
          @change="${this._handleChange}"
          aria-label="${this.getAttribute('aria-label') || this.text || 'Checkbox'}"
        />
        <span class="checkbox">
          ${isSelected
            ? html`
                <svg
                  class="checkmark"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.16699 10.8333L7.50033 14.1666L15.8337 5.83331"
                    stroke="var(--checkmark-color, white)"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              `
            : isIndeterminate
            ? html`<span class="minus"></span>`
            : ''}
        </span>
        ${this.position === 'right' ? html`
          <slot name="label">
            ${this.text ? html`<span class="label">${this.text}</span>` : ''}
          </slot>
        ` : ''}
        <slot></slot>
      </label>
    `;
  }

  _handleChange(e) {
    if (this.disabled) return;
    this.checked = e.target.checked;
    this.indeterminate = false;
    this.state = this.checked ? 'selected' : 'default';
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }
}

customElements.define('studio-checkbox', StudioCheckbox);